import FormTextField from '@common/components/Form/FormTextField';
import { BaseFormProps } from '@common/components/Form/index.types';
import useAppForm from '@common/components/Form/useAppForm';
import {
  IExactScoreBets,
  ISuperExactScoreEvent,
  ISuperExactScoreRequest,
} from '@features/super-exact-score/api/superExactScore.type';
import { yupResolver } from '@hookform/resolvers/yup';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import getUnixTime from 'date-fns/getUnixTime';
import React, { FC, useEffect } from 'react';
import { SubmitHandler } from 'react-hook-form';
import { IMaskInput } from 'react-imask';
import { FormattedMessage } from 'react-intl';
import * as Yup from 'yup';

type ExactScoreFormProps = BaseFormProps<ISuperExactScoreRequest>;

type ISuperExactScoreFormProps = ExactScoreFormProps & {
  event: ISuperExactScoreEvent;
  exactScoreId: number;
  selectedIndex: number;
  predictedScore: string | null
  exactScoreBets?: IExactScoreBets;
}
interface IExactScoreSchema {
  result1?: number | string;
  result2?: number | string;
}


interface InputMaskProps {
  onChange: (event: { target: { name: string; value: string } }) => void;
  name: string;
}

const InputMask = React.forwardRef<HTMLElement, InputMaskProps>(
  function InputMask(props) {
    const { onChange, ...other } = props;
    return (
      <IMaskInput
        {...other}
        mask="00"
        definitions={{
          '#': /[1-9]/,
        }}
        onAccept={(value: any) => onChange({ target: { name: props.name, value } })}
        overwrite
      />
    );
  },
);

const SuperExactScoreForm: FC<ISuperExactScoreFormProps> = ({selectedIndex,  event, predictedScore, errors, exactScoreId, onSubmit, exactScoreBets }) => {
  const currentTimestamp = getUnixTime(new Date());
  const isFinished = !(exactScoreBets && exactScoreBets[event.id]) && (!!event.eventResult || event.date * 1000 < Date.now())

  const exactScoreSchema = Yup.object({
    result1: Yup.string().required(),
    result2: Yup.string().required(),
  });

  const schema = exactScoreSchema.pick([ 'result1', 'result2' ]);

  const { control, setValue,  handleSubmit } = useAppForm<IExactScoreSchema>({
    resolver: yupResolver(schema),
    defaultValues: {
      result1: '',
      result2: '',
    },
  }, errors);

  useEffect(() => {
    const results = predictedScore?.split(':');
    const result1 = results ? results[0] : '';
    const result2 = results ? results[1] : '';

    setValue('result1', result1);
    setValue('result2', result2);
  }, [predictedScore, selectedIndex, setValue]);

  const submit: SubmitHandler<IExactScoreSchema> = (values) => {
    const { result1, result2 } = values;

    onSubmit({
      eventId: event.id,
      exactScoreId: exactScoreId,
      predictedScore: `${result1}:${result2}`,
    });
  };

  return (
    <Box marginTop={5}>
      <form onSubmit={handleSubmit(submit)}>
        <Box marginBottom={3}>
          {event.onlineTime && (
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <FormTextField
                type="tel" name="result1" control={control}
                             label={event.host}
                             placeholder="0"
                             disabled={isFinished}
                             TextFieldProps={{
                               inputProps: {
                                 style: {
                                   color: '#fff',
                                   fontWeight: 600,
                                 },
                               },
                               InputLabelProps: {
                                 style: {
                                   fontSize: '1.2em',
                                   color: '#e5e5e5',
                                 },
                               },
                               InputProps: {
                                 inputComponent: InputMask as any,
                               },
                             }}
              />
              <Typography fontSize="2em" fontWeight="700" marginX={3} marginBottom={3}>
                :
              </Typography>
              <FormTextField
                type="tel" name="result2" control={control}
                             placeholder="0"
                             disabled={isFinished}
                             label={event.guest}
                             TextFieldProps={{
                               inputProps: {
                                 style: {
                                   color: '#fff',
                                   fontWeight: 600,
                                 },
                               },
                               InputLabelProps: {
                                 style: {
                                   fontSize: '1.2em',
                                   color: '#e5e5e5',
                                 },
                               },
                               InputProps: {
                                 inputComponent: InputMask as any,
                               },
                             }}
              />
            </Box>
          )}
        </Box>

        {!isFinished && !predictedScore && !event.eventResult && !(Number(event.onlineTime) > currentTimestamp) && (
          <Button type="submit" fullWidth size="large" variant="contained" color="success" disabled={isFinished}>
            <FormattedMessage id="MAKE_PREDICTION"/>
          </Button>
        )}
      </form>
    </Box>
  );
};

export default SuperExactScoreForm;
